import type { RaspadinhaCustomDocument } from '@/types/raspadinha-campaign'

export const MAX_CUSTOM_DOCUMENTS = 3
export const MAX_CUSTOM_DOCUMENT_TITLE_LENGTH = 120

function buildCustomDocumentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `custom-document-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function createEmptyCustomDocument(): RaspadinhaCustomDocument {
  return {
    id: buildCustomDocumentId(),
    title: '',
    pdf_url: '',
    pdf_name: '',
  }
}

export function ensureCustomDocuments(
  documents: RaspadinhaCustomDocument[] | undefined
): RaspadinhaCustomDocument[] {
  return (documents ?? []).slice(0, MAX_CUSTOM_DOCUMENTS).map((document) => ({
    id: document.id,
    title: document.title ?? '',
    pdf_url: document.pdf_url ?? '',
    pdf_name: document.pdf_name ?? '',
  }))
}

export function canAddCustomDocument(documents: RaspadinhaCustomDocument[]): boolean {
  return documents.length < MAX_CUSTOM_DOCUMENTS
}

export function addCustomDocument(
  documents: RaspadinhaCustomDocument[]
): RaspadinhaCustomDocument[] {
  if (!canAddCustomDocument(documents)) {
    return documents
  }
  return [...documents, createEmptyCustomDocument()]
}

export function updateCustomDocument(
  documents: RaspadinhaCustomDocument[],
  documentId: string,
  patch: Partial<Omit<RaspadinhaCustomDocument, 'id'>>
): RaspadinhaCustomDocument[] {
  return documents.map((document) => {
    if (document.id !== documentId) {
      return document
    }

    return {
      ...document,
      ...patch,
      title:
        patch.title !== undefined
          ? patch.title.slice(0, MAX_CUSTOM_DOCUMENT_TITLE_LENGTH)
          : document.title,
    }
  })
}

export function removeCustomDocument(
  documents: RaspadinhaCustomDocument[],
  documentId: string
): RaspadinhaCustomDocument[] {
  return documents.filter((document) => document.id !== documentId)
}

export function isCustomDocumentDraft(document: RaspadinhaCustomDocument): boolean {
  return !document.title.trim() && !document.pdf_url.trim()
}

export function pruneCustomDocumentDrafts(
  documents: RaspadinhaCustomDocument[]
): RaspadinhaCustomDocument[] {
  return documents.filter((document) => !isCustomDocumentDraft(document))
}

export function validateCustomDocuments(documents: RaspadinhaCustomDocument[]): string | null {
  if (documents.length > MAX_CUSTOM_DOCUMENTS) {
    return `É permitido no máximo ${MAX_CUSTOM_DOCUMENTS} documentos personalizados.`
  }

  for (const [index, document] of documents.entries()) {
    if (isCustomDocumentDraft(document)) {
      continue
    }

    const position = index + 1
    const title = document.title.trim()

    if (!title) {
      return `Informe o título do documento personalizado #${position}.`
    }

    if (!document.pdf_url.trim()) {
      return `Envie o PDF do documento "${title}".`
    }
  }

  return null
}
